import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { SignInContext } from '@modules/users';

import { DEFAULT_LOADING_MESSAGES, DEFAULT_REVIEW_DURATION_MS } from '../constants';
import { getReviewBases, isReviewRunning, resolveScenario } from '../helpers';
import { TFindingState, TReview, TReviewBasis, TReviewMockConfig, TReviewScenario, TStartReviewInput } from '../types';

const REVIEW_TICK_MS = 400;

const toFindingStates = (scenario: TReviewScenario): TFindingState[] =>
  (scenario.findings ?? []).map((finding) => ({ ...finding, status: finding.status ?? 'unresolved' }));

const getScenarioDuration = (scenario: TReviewScenario): number =>
  scenario.durationSeconds ? scenario.durationSeconds * 1000 : DEFAULT_REVIEW_DURATION_MS;

const buildScopeLabel = (scenario: TReviewScenario, scopeItems: { name: string }[]): string => {
  if (scenario.context?.scopeLabel) return scenario.context.scopeLabel;
  if (scopeItems.length === 0) return 'Selected documents';
  if (scopeItems.length === 1) return scopeItems[0].name;
  return `${scopeItems[0].name} and ${scopeItems.length - 1} more`;
};

type TReviewStore = {
  config?: TReviewMockConfig;
  reviewBases: TReviewBasis[];
  reviews: TReview[];
  now: number;
  currentUser: string;
  currentUserId?: string;
  getReview: (reviewId: string) => TReview | undefined;
  startReview: (input: TStartReviewInput) => string;
  acceptChange: (reviewId: string, findingId: string) => void;
  dismissFinding: (reviewId: string, findingId: string, reason: string) => void;
  assignFinding: (reviewId: string, findingId: string, assignee: string, dueDate?: string, assigneeId?: string) => void;
  addComment: (reviewId: string, findingId: string, text: string) => void;
  reopenFinding: (reviewId: string, findingId: string) => void;
};

const ReviewStoreContext = createContext<TReviewStore | undefined>(undefined);

export const ReviewProvider = ({ children }: { children: ReactNode }) => {
  const { user, organization } = useContext(SignInContext);
  const config = organization.mock?.review as unknown as TReviewMockConfig | undefined;
  const currentUser = user.name || user.mail;
  const currentUserId = user._id;

  const [reviews, setReviews] = useState<TReview[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const reviewCounter = useRef(0);
  const activityCounter = useRef(0);

  const hasRunningReview = reviews.some((review) => isReviewRunning(review, now));

  // The clock only runs while a review is running, and it lives above the routes
  // so a review keeps progressing while the user is on another page.
  useEffect(() => {
    if (!hasRunningReview) return undefined;
    const interval = window.setInterval(() => setNow(Date.now()), REVIEW_TICK_MS);
    return () => window.clearInterval(interval);
  }, [hasRunningReview]);

  const updateFinding = useCallback(
    (reviewId: string, findingId: string, update: (finding: TFindingState) => TFindingState) => {
      const createdAt = Date.now();
      const activityId = `status-${createdAt}-${++activityCounter.current}`;
      setReviews((previous) =>
        previous.map((review) => {
          if (review.id !== reviewId) return review;
          const original = review.findings.find((finding) => finding.id === findingId);
          if (!original) return review;
          const updated = update(original);
          const statusUpdates =
            original.status === updated.status
              ? review.statusUpdates
              : [
                  {
                    id: activityId,
                    findingId,
                    from: original.status,
                    to: updated.status,
                    author: currentUser,
                    authorId: currentUserId,
                    createdAt,
                  },
                  ...(review.statusUpdates ?? []),
                ];
          return {
            ...review,
            findings: review.findings.map((finding) => (finding.id === findingId ? updated : finding)),
            statusUpdates,
          };
        })
      );
    },
    [currentUser, currentUserId]
  );

  const startReview = useCallback(
    (input: TStartReviewInput): string => {
      const scenario = resolveScenario(config, input.reviewBasisIds, input.productId);
      reviewCounter.current += 1;
      const id = `review-${Date.now()}-${reviewCounter.current}`;
      const review: TReview = {
        id,
        name: scenario.name,
        reviewType: input.reviewType,
        reviewBasisIds: input.reviewBasisIds,
        scopeItems: input.scopeItems,
        scopeLabel: buildScopeLabel(scenario, input.scopeItems),
        instructions: input.instructions,
        productId: input.productId,
        productName: input.productName ?? scenario.context?.productName,
        productContext: input.productContext ?? scenario.context?.productContext,
        documentsAnalyzed: scenario.context?.documentsAnalyzed ?? input.scopeItems.length,
        startedAt: Date.now(),
        startedBy: currentUser,
        startedById: currentUserId,
        durationMs: getScenarioDuration(scenario),
        loadingMessages: scenario.loadingMessages?.length ? scenario.loadingMessages : DEFAULT_LOADING_MESSAGES,
        findings: toFindingStates(scenario),
      };
      setReviews((previous) => [review, ...previous]);
      setNow(Date.now());
      return id;
    },
    [config, currentUser, currentUserId]
  );

  const value = useMemo<TReviewStore>(
    () => ({
      config,
      reviewBases: getReviewBases(config),
      reviews,
      now,
      currentUser,
      currentUserId,
      getReview: (reviewId: string) => reviews.find((review) => review.id === reviewId),
      startReview,
      acceptChange: (reviewId, findingId) =>
        updateFinding(reviewId, findingId, (finding) => ({
          ...finding,
          status: 'resolved',
          dismissReason: undefined,
        })),
      dismissFinding: (reviewId, findingId, reason) =>
        updateFinding(reviewId, findingId, (finding) => ({ ...finding, status: 'dismissed', dismissReason: reason })),
      assignFinding: (reviewId, findingId, assignee, dueDate, assigneeId) =>
        updateFinding(reviewId, findingId, (finding) => ({
          ...finding,
          status: 'assigned',
          assignee,
          assigneeId,
          dueDate,
        })),
      addComment: (reviewId, findingId, text) => {
        if (!text.trim()) return;
        const comment = {
          id: `comment-${Date.now()}-${++activityCounter.current}`,
          author: currentUser,
          authorId: currentUserId,
          text: text.trim(),
          createdAt: Date.now(),
        };
        updateFinding(reviewId, findingId, (finding) => ({
          ...finding,
          comments: [...(finding.comments ?? []), comment],
        }));
      },
      reopenFinding: (reviewId, findingId) =>
        updateFinding(reviewId, findingId, (finding) => ({
          ...finding,
          status: 'unresolved',
          assignee: undefined,
          assigneeId: undefined,
          dueDate: undefined,
          dismissReason: undefined,
        })),
    }),
    [config, reviews, now, currentUser, currentUserId, startReview, updateFinding]
  );

  return <ReviewStoreContext.Provider value={value}>{children}</ReviewStoreContext.Provider>;
};

export const useReviewStore = (): TReviewStore => {
  const store = useContext(ReviewStoreContext);
  if (!store) throw new Error('useReviewStore must be used inside ReviewProvider');
  return store;
};
